import { internalFetch } from '../utils/fetchHelper';

export interface IChartData {
  values: Map<string, number>;
}

// The three backend function apps that can each contribute dashboard
// components: AccessControl (/a), IssueFunction (/i) and Portal (/p).
export type DashboardSource = 'a' | 'i' | 'p';
export const DASHBOARD_SOURCES: DashboardSource[] = ['a', 'i', 'p'];

export type DashboardComponentType = 'COUNT' | 'BARCHART';

export interface IDashboardComponent {
  name: string;
  type: DashboardComponentType;
}

// A component plus the source it was discovered from, so its data is later
// fetched from the same backend that advertised it.
export interface IDashboardComponentWithSource extends IDashboardComponent {
  source: DashboardSource;
}

interface IGetDashboardComponentsResponse {
  components: IDashboardComponent[];
}

const getDashboardComponentsForSource = async (
  abortController: AbortController,
  source: DashboardSource
): Promise<IDashboardComponentWithSource[]> => {
  const res: IGetDashboardComponentsResponse = await internalFetch(
    `${
      import.meta.env.VITE_BACKEND_API_URL
    }/${source}/dashboard/components?code=${
      import.meta.env.VITE_BACKEND_API_KEY
    }`,
    'GET',
    abortController
  );
  return (res?.components ?? []).map((c) => ({ ...c, source }));
};

// Fetch the available components from all three sources in parallel and merge
// them. Resilient: a single source failing does not drop the others.
// De-duplicated by name (first source wins) so the customize list never shows
// the same component twice.
export const getDashboardComponents = async (
  abortController: AbortController
): Promise<IDashboardComponentWithSource[]> => {
  const results = await Promise.allSettled(
    DASHBOARD_SOURCES.map((source) =>
      getDashboardComponentsForSource(abortController, source)
    )
  );

  const merged: IDashboardComponentWithSource[] = [];
  const seen = new Set<string>();
  for (const result of results) {
    if (result.status !== 'fulfilled') continue;
    for (const component of result.value) {
      if (!component.name || seen.has(component.name)) continue;
      seen.add(component.name);
      merged.push(component);
    }
  }
  return merged;
};

// Fetch a single component's data from the source that owns it. `options` is an
// optional plain value (e.g. "Cat", "13"); when present it is wrapped as
// {"options": value}, JSON-encoded and URL-encoded into the `options` param,
// per the BE contract for /dashboard/component.
export const getDashboardComponentData = async (
  abortController: AbortController,
  source: DashboardSource,
  componentCode: string,
  optionValue?: string
): Promise<IChartData> => {
  const base = import.meta.env.VITE_BACKEND_API_URL;
  const key = import.meta.env.VITE_BACKEND_API_KEY;
  let url = `${base}/${source}/dashboard/component?componentCode=${encodeURIComponent(
    componentCode
  )}`;
  if (optionValue !== undefined && optionValue !== '') {
    const encoded = encodeURIComponent(
      JSON.stringify({ options: optionValue })
    );
    url += `&options=${encoded}`;
  }
  url += `&code=${key}`;
  return internalFetch(url, 'GET', abortController);
};

export const getChartsData = async (
  abortController: AbortController,
  entityType: string,
  options?: string
): Promise<IChartData> => {
  return internalFetch(
    `${
      import.meta.env.VITE_BACKEND_API_URL
    }/p/chart/${entityType}?options=${options}&code=${
      import.meta.env.VITE_BACKEND_API_KEY
    }`,
    'GET',
    abortController
  );
};

export const getDashboardComponentOptions = async (
  abortController: AbortController,
  componentCode: string,
  source: DashboardSource = 'p'
): Promise<IChartData> => {
  return internalFetch(
    `${
      import.meta.env.VITE_BACKEND_API_URL
    }/${source}/dashboard/component/${encodeURIComponent(
      componentCode
    )}/options?code=${import.meta.env.VITE_BACKEND_API_KEY}`,
    'GET',
    abortController
  );
};

export const getDashboardPanels = async (
  abortController: AbortController,
  panelName: string,
  options: string
): Promise<IChartData> => {
  return internalFetch(
    `${
      import.meta.env.VITE_BACKEND_API_URL
    }/p/dashboardPanels/${panelName}?options=${options}&code=${
      import.meta.env.VITE_BACKEND_API_KEY
    }`,
    'GET',
    abortController
  );
};
